import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import { Policy, PolicyType } from "../../types/admin";

type PoliciesState = {
  list: Policy[];
  loadingList: boolean;
  listError: string | null;
  current: Policy | null;
  loadingCurrent: boolean;
  currentError: string | null;
  updating: boolean;
  updateError: string | null;
};

const initialState: PoliciesState = {
  list: [],
  loadingList: false,
  listError: null,
  current: null,
  loadingCurrent: false,
  currentError: null,
  updating: false,
  updateError: null,
};

export const fetchPolicies = createAsyncThunk<{ items: Policy[] }, void, { rejectValue: string }>(
  "policies/fetchPolicies",
  async (_, { rejectWithValue }) => {
    try {
      return await apiRequest<{ items: Policy[] }>("/admin/policies");
    } catch (error) {
      const message = error instanceof Error ? error.message : "정책 목록 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const fetchPolicyDetail = createAsyncThunk<Policy, PolicyType, { rejectValue: string }>(
  "policies/fetchPolicyDetail",
  async (policyType, { rejectWithValue }) => {
    try {
      return await apiRequest<Policy>(`/admin/policies/${policyType}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "정책 상세 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const updatePolicy = createAsyncThunk<
  Policy,
  { policyType: PolicyType; title?: string; content?: string },
  { rejectValue: string }
>("policies/updatePolicy", async ({ policyType, ...payload }, { rejectWithValue }) => {
  try {
    return await apiRequest<Policy>(`/admin/policies/${policyType}`, {
      method: "PATCH",
      body: payload,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "정책 수정에 실패했습니다.";
    return rejectWithValue(message);
  }
});

const policiesSlice = createSlice({
  name: "policies",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicies.pending, (state) => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.loadingList = false;
        state.list = action.payload.items;
      })
      .addCase(fetchPolicies.rejected, (state, action) => {
        state.loadingList = false;
        state.listError = action.payload ?? "정책 목록 조회에 실패했습니다.";
      })
      .addCase(fetchPolicyDetail.pending, (state) => {
        state.loadingCurrent = true;
        state.currentError = null;
      })
      .addCase(fetchPolicyDetail.fulfilled, (state, action) => {
        state.loadingCurrent = false;
        state.current = action.payload;
      })
      .addCase(fetchPolicyDetail.rejected, (state, action) => {
        state.loadingCurrent = false;
        state.currentError = action.payload ?? "정책 상세 조회에 실패했습니다.";
      })
      .addCase(updatePolicy.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        state.updating = false;
        state.current = action.payload;
        state.list = state.list.map((policy) =>
          policy.policy_type === action.payload.policy_type ? action.payload : policy
        );
      })
      .addCase(updatePolicy.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload ?? "정책 수정에 실패했습니다.";
      });
  },
});

export default policiesSlice.reducer;
